import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { UniversalMultiCombobox } from '@/components/common/UniversalMultiCombobox';
import type { EventFormValues } from '../schemas';

interface MatterDetails {
  id: number;
  name?: string;
  judgeName?: string;
  courtName?: string;
  courtEmail?: string;
  courtPhone?: string;
}

interface EventDialogFormProps {
  form: UseFormReturn<EventFormValues>;
  selectedMatter: MatterDetails | undefined;
  onMatterSelect: (item: MatterDetails | undefined) => void;
  onSubmit: (values: EventFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function EventDialogForm({
  form,
  selectedMatter,
  onMatterSelect,
  onSubmit,
  onCancel,
  isSubmitting,
}: EventDialogFormProps) {
  const { t } = useTranslation();
  const watchTarget = form.watch('target');
  const watchClientId = form.watch('clientId');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('description')} *</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t('enterDescription')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('location')}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('enterLocation')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="startsAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('startsAt')} *</FormLabel>
              <FormControl>
                <DateTimePicker value={field.value} onChange={field.onChange} mode="datetime" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="endsAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('endsAt')} *</FormLabel>
              <FormControl>
                <DateTimePicker value={field.value} onChange={field.onChange} mode="datetime" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="target"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('target.target')} *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('target.target')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="FIRM_EVENT">{t('target.FIRM_EVENT')}</SelectItem>
                  <SelectItem value="NEW_CLIENT">{t('target.NEW_CLIENT')}</SelectItem>
                  <SelectItem value="CASE_MEETING">{t('target.CASE_MEETING')}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchTarget === 'CASE_MEETING' && (
          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('selectClient')}</FormLabel>
                <FormControl>
                  <UniversalCombobox
                    url="/clients"
                    labelKey="name"
                    labelKeys={['lastname', 'name', 'middlename']}
                    valueKey="id"
                    selectedId={field.value}
                    onSelectItem={(item) => field.onChange(item?.id ?? undefined)}
                    placeholder={t('selectClient')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {watchTarget === 'CASE_MEETING' && (
          <FormField
            control={form.control}
            name="matterId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('selectMatter')} *</FormLabel>
                <FormControl>
                  <UniversalCombobox
                    url="/matters"
                    listUrl="/matters/select-box"
                    labelKey="name"
                    valueKey="id"
                    selectedId={field.value}
                    params={{ clientId: watchClientId }}
                    onSelectItem={(item) => {
                      onMatterSelect(item as MatterDetails | undefined);
                      field.onChange(item?.id ?? undefined);
                    }}
                    placeholder={t('selectMatter')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {selectedMatter && watchTarget === 'CASE_MEETING' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="grid w-full items-center gap-1.5">
              <Label>{t('judgeName')}</Label>
              <Input disabled value={selectedMatter.judgeName || '-'} />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label>{t('courtName')}</Label>
              <Input disabled value={selectedMatter.courtName || '-'} />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label>{t('courtEmail')}</Label>
              <Input disabled value={selectedMatter.courtEmail || '-'} />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label>{t('courtPhone')}</Label>
              <Input disabled value={selectedMatter.courtPhone || '-'} />
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="attendees"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('attendees')}</FormLabel>
              <FormControl>
                <UniversalMultiCombobox
                  url="/users"
                  listUrl="/users/select-box"
                  labelKey="name"
                  valueKey="id"
                  selectedIds={field.value ?? []}
                  params={{ status: 'ACTIVE' }}
                  onSelectItem={(items) => {
                    const newIds = items.map((item) => item.id as number);
                    field.onChange(newIds);
                  }}
                  placeholder={t('selectUsers')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
